import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  VideoOff,
  MicOff,
  MessageCircle,
  Clock,
  Target,
  CheckCircle,
  Play,
  Pause,
  RotateCcw,
  Maximize2,
  LayoutGrid,
  Loader2,
  Download
} from 'lucide-react';
import CallControls from './CallControls';
import useWebRTC from '../hooks/useWebRTC';
import useCallRecorder, { canRecordCalls } from '../hooks/useCallRecorder';
import { playMessageSentTone, playMessageReceivedTone } from '../utils/sounds';

const SessionPage = ({ socket, session, user, onEndSession }) => {
  // The background itself is painted once by BackgroundRenderer, which wraps
  // this page — nothing to read here any more.
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [goals, setGoals] = useState({ user: '', partner: '' });
  const [newGoal, setNewGoal] = useState('');
  const [callEnded, setCallEnded] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [showChat, setShowChat] = useState(false);
  // Auto-detect mobile and default to stacked layout
  const [cameraLayout, setCameraLayout] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return 'stacked';
    }
    return 'side-by-side';
  }); // 'side-by-side', 'stacked', 'stacked-with-chat'
  
  // Responsive layout adjustment - auto-stack on narrow windows
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768 && cameraLayout === 'side-by-side') {
        setCameraLayout('stacked');
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [cameraLayout]);
  
  const messagesEndRef = useRef(null);
  const timerRef = useRef(null);

  // WebRTC hook for video/audio
  const {
    localVideoRef,
    remoteVideoRef,
    localStream,
    remoteStream,
    isVideoEnabled,
    isAudioEnabled,
    mediaError,
    connectionState,
    isScreenSharing,
    isBlurEnabled,
    isBlurLoading,
    blurError,
    canBlur,
    toggleVideo,
    toggleAudio,
    toggleScreenShare,
    toggleBlur,
    cleanup
  } = useWebRTC(socket, session.id, user.id < session.partner.id);
  const canScreenShare = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

  const {
    isRecording,
    recordingSeconds,
    downloadUrl,
    downloadFilename,
    error: recordingError,
    startRecording,
    stopRecording,
    clearDownload
  } = useCallRecorder({ localVideoRef, remoteVideoRef, localStream, remoteStream });

  // 'failed' means ICE gave up finding a route between the two networks —
  // no amount of waiting fixes it, so don't keep showing a spinner.
  const connectionFailed = connectionState === 'failed';
  // Dropped mid-call (network blip, or the app was backgrounded on a
  // phone). Recovery runs automatically; say so rather than leaving a
  // frozen picture with no explanation.
  const reconnecting = connectionState === 'disconnected' && !!remoteStream;

  const formatRecordingTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // If a recording is in progress when the call ends, stop it and hold off
  // navigating away until the user has had a chance to grab the file —
  // otherwise the recording finishes processing after SessionPage has
  // already unmounted and there's nowhere to show the download.
  const finishSession = useCallback(() => {
    if (isRecording) {
      stopRecording();
      setCallEnded(true);
    } else {
      cleanup();
      onEndSession();
    }
  }, [isRecording, stopRecording, cleanup, onEndSession]);

  useEffect(() => {
    if (socket) {
      socket.on('session-message', (message) => {
        playMessageReceivedTone();
        setMessages(prev => [...prev, message]);
      });

      socket.on('goal-update', (data) => {
        setGoals(prev => ({
          ...prev,
          [data.from === user.id ? 'user' : 'partner']: data.goal
        }));
      });

      socket.on('timer-sync', (timerData) => {
        setTimer(timerData.time);
        setIsTimerRunning(timerData.isRunning);
      });

      socket.on('friend-added', (friend) => {
        console.log('Friend added successfully:', friend);
        alert(`Successfully added ${friend.name} as a friend!`);
      });

      socket.on('session-ended', () => {
        console.log('Session ended by partner');
        finishSession();
      });

      socket.on('partner-disconnected', () => {
        console.log('Partner disconnected');
        finishSession();
      });

      return () => {
        socket.off('session-message');
        socket.off('goal-update');
        socket.off('timer-sync');
        socket.off('friend-added');
        socket.off('session-ended');
        socket.off('partner-disconnected');
      };
    }
  }, [socket, user.id, onEndSession, cleanup, finishSession]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isTimerRunning) {
      timerRef.current = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [isTimerRunning]);

  const sendMessage = (e) => {
    e.preventDefault();
    if (newMessage.trim() && socket) {
      const message = {
        text: newMessage,
        type: 'chat'
      };
      socket.emit('session-message', message);
      playMessageSentTone();
      setMessages(prev => [...prev, {
        ...message,
        from: user,
        timestamp: new Date()
      }]);
      setNewMessage('');
    }
  };

  const setGoal = (e) => {
    e.preventDefault();
    if (newGoal.trim()) {
      setGoals(prev => ({ ...prev, user: newGoal }));
      if (socket) {
        socket.emit('goal-update', { goal: newGoal });
        socket.emit('session-message', {
          type: 'goal',
          text: `Set goal: ${newGoal}`
        });
      }
      setNewGoal('');
    }
  };

  const addFriend = () => {
    if (socket && session.partner) {
      console.log('Adding friend:', session.partner);
      // The partner's STABLE id. It used to fall back to socketId, which the
      // server then failed to resolve — see the add-friend handler.
      const partnerUserId = session.partner.id;
      if (partnerUserId) {
        socket.emit('add-friend', partnerUserId);
        // Add visual feedback
        alert('Friend request sent! Check your friends list.');
      } else {
        console.error('No valid partner ID found:', session.partner);
        alert('Unable to add friend - partner ID not found');
      }
    }
  };

  // Called once the user has seen the post-call download prompt (or
  // dismissed it) — actually navigates away.
  const finalizeAndLeave = () => {
    clearDownload();
    cleanup();
    onEndSession();
  };

  const endSession = () => {
    if (socket) {
      socket.emit('end-session');
    }
    // Small delay to ensure server processes the event
    setTimeout(() => {
      finishSession();
    }, 100);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Safety check for background
  // Empty on purpose: BackgroundRenderer wraps this page and paints a single
  // fixed background layer for the whole app. Painting it again here stacked
  // a second, differently-cropped copy on top — the seam visible on mobile.
  const backgroundStyle = { minHeight: '100vh' };

  // The call has ended (either party) with a recording in flight — hold
  // here until it finishes processing so there's actually a chance to grab
  // it, rather than losing it the instant this page would otherwise unmount.
  if (callEnded) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={backgroundStyle}>
        <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Call ended</h2>
          {downloadUrl ? (
            <>
              <p className="text-gray-600 mb-6">Your recording is ready.</p>
              <a
                href={downloadUrl}
                download={downloadFilename}
                className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold mb-4"
              >
                <Download className="w-5 h-5" />
                Download Recording
              </a>
              <button
                onClick={finalizeAndLeave}
                className="block w-full text-gray-500 hover:text-gray-700 font-medium mt-2"
              >
                Continue without downloading
              </button>
            </>
          ) : recordingError ? (
            <>
              <p className="text-gray-600 mb-6">{recordingError}</p>
              <button
                onClick={finalizeAndLeave}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold"
              >
                Continue
              </button>
            </>
          ) : (
            <div className="flex items-center justify-center gap-2 text-gray-600 mb-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Finishing recording...</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={backgroundStyle}>
      {blurError && (
        <div className="fixed bottom-4 inset-x-4 z-50 mx-auto max-w-md bg-red-600/95 text-white text-sm rounded-xl px-4 py-3 shadow-2xl">
          {blurError}
        </div>
      )}
      <div className="max-w-6xl mx-auto p-4">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <img 
                src="/logo-compressed.png" 
                alt="BodyDouble Logo" 
                className="max-w-12 max-h-12 w-auto h-auto"
              />
              <div>
                <h1 className="text-2xl font-bold text-white">Body Doubling Session</h1>
                <p className="text-white/80">with {session.partner.name}</p>
              </div>
            </div>
            
            {/* Timer */}
            <div className="flex items-center space-x-4">
              {isRecording && (
                <div className="flex items-center space-x-2 bg-red-600/90 rounded-lg px-3 py-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse"></span>
                  <span className="text-white font-mono text-sm">{formatRecordingTime(recordingSeconds)}</span>
                </div>
              )}
              <div className="bg-white/20 rounded-lg px-4 py-2">
                <div className="flex items-center space-x-2">
                  <Clock className="w-5 h-5 text-white" />
                  <span className="text-white font-mono text-xl">{formatTime(timer)}</span>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    const newRunning = !isTimerRunning;
                    setIsTimerRunning(newRunning);
                    if (socket) {
                      socket.emit('timer-sync', { time: timer, isRunning: newRunning });
                    }
                  }}
                  className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors"
                >
                  {isTimerRunning ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => {
                    setTimer(0);
                    setIsTimerRunning(false);
                    if (socket) {
                      socket.emit('timer-sync', { time: 0, isRunning: false });
                    }
                  }}
                  className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors"
                >
                  <RotateCcw className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Recording ready — shown mid-call too, not just after the call
            ends, since stopping a recording without ending the session
            should still let you actually get the file. */}
        {downloadUrl && (
          <div className="bg-blue-500/90 backdrop-blur-md rounded-2xl p-4 mb-6 flex items-center justify-between gap-4 flex-wrap">
            <span className="text-white font-medium">Your recording is ready.</span>
            <div className="flex items-center gap-3">
              <a
                href={downloadUrl}
                download={downloadFilename}
                className="inline-flex items-center gap-2 bg-white text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                <Download className="w-4 h-4" />
                Download
              </a>
              <button
                onClick={clearDownload}
                className="text-white/80 hover:text-white text-sm font-medium"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Layout Toggle Button */}
        <div className="flex justify-end mb-4 z-50 relative">
          <div className="bg-black/70 backdrop-blur-md rounded-lg p-2 flex space-x-2 border-2 border-white/40 shadow-2xl">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCameraLayout('side-by-side');
              }}
              className={`p-2 rounded transition-all duration-200 ${
                cameraLayout === 'side-by-side' 
                  ? 'bg-blue-500 text-white shadow-lg scale-110' 
                  : 'bg-white/40 text-white hover:bg-white/60 hover:scale-105'
              }`}
              title="Side by side layout"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCameraLayout('stacked');
              }}
              className={`p-2 rounded transition-all duration-200 ${
                cameraLayout === 'stacked' 
                  ? 'bg-blue-500 text-white shadow-lg scale-110' 
                  : 'bg-white/40 text-white hover:bg-white/60 hover:scale-105'
              }`}
              title="Stacked full screen"
            >
              <Maximize2 className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCameraLayout('stacked-with-chat');
              }}
              className={`p-2 rounded transition-all duration-200 ${
                cameraLayout === 'stacked-with-chat' 
                  ? 'bg-blue-500 text-white shadow-lg scale-110' 
                  : 'bg-white/40 text-white hover:bg-white/60 hover:scale-105'
              }`}
              title="Stacked with chat"
            >
              <MessageCircle className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Dynamic Layout Container */}
        <>
        {cameraLayout === 'stacked' ? (
          /* Full Screen Stacked Layout */
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
              <div className="space-y-4">
                {/* Partner Video (Top) */}
                <div className="relative bg-gray-800 rounded-xl aspect-video overflow-hidden">
                  <video
                    ref={remoteVideoRef}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  {!remoteStream && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="bg-purple-500 rounded-full p-4 w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                          <span className="font-bold text-xl">{session.partner.name[0].toUpperCase()}</span>
                        </div>
                        <p className="font-medium">{session.partner.name}</p>
                        {connectionFailed ? (
                          <p className="mt-2 text-red-300 text-xs max-w-[14rem] mx-auto">
                            Couldn&apos;t connect to {session.partner.name}. Your networks may be
                            blocking the connection — try again, or switch off a VPN if you use one.
                          </p>
                        ) : (
                          <div className="flex items-center justify-center gap-1.5 mt-2 text-white/70 text-xs">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Connecting...</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="absolute top-2 left-2 bg-black/50 rounded px-2 py-1">
                    <span className="text-white text-xs font-medium">{session.partner.name}</span>
                    {reconnecting && (
                      <span className="ml-2 text-amber-300 text-xs">Reconnecting...</span>
                    )}
                  </div>
                </div>

                {/* User Video (Bottom) */}
                <div className="relative bg-gray-800 rounded-xl aspect-video overflow-hidden">
                  <video
                    ref={localVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  {!localStream && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="bg-blue-500 rounded-full p-4 w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                          <span className="font-bold text-xl">{user.name[0].toUpperCase()}</span>
                        </div>
                        <p className="font-medium">{user.name} (You)</p>
                      </div>
                    </div>
                  )}
                  {!isVideoEnabled && localStream && (
                    <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="bg-blue-500 rounded-full p-4 w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                          <span className="font-bold text-xl">{user.name[0].toUpperCase()}</span>
                        </div>
                        <p className="font-medium">{user.name} (You)</p>
                      </div>
                    </div>
                  )}
                  {mediaError && (
                    <div className="absolute inset-0 bg-gray-900/95 flex items-center justify-center p-4">
                      <div className="text-white text-center text-sm">
                        <VideoOff className="w-6 h-6 mx-auto mb-2 text-red-400" />
                        <p>{mediaError}</p>
                      </div>
                    </div>
                  )}
                  <div className="absolute top-2 left-2 bg-black/50 rounded px-2 py-1">
                    <span className="text-white text-xs font-medium">You</span>
                  </div>
                  {!isVideoEnabled && (
                    <div className="absolute top-2 right-2 bg-red-500 rounded-full p-1">
                      <VideoOff className="w-4 h-4 text-white" />
                    </div>
                  )}
                  {!isAudioEnabled && (
                    <div className="absolute bottom-2 right-2 bg-red-500 rounded-full p-1">
                      <MicOff className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              </div>

              {/* Controls */}
              <CallControls
                isVideoEnabled={isVideoEnabled}
                toggleVideo={toggleVideo}
                isAudioEnabled={isAudioEnabled}
                toggleAudio={toggleAudio}
                canScreenShare={canScreenShare}
                isScreenSharing={isScreenSharing}
                toggleScreenShare={toggleScreenShare}
                canBlur={canBlur}
                isBlurEnabled={isBlurEnabled}
                isBlurLoading={isBlurLoading}
                toggleBlur={toggleBlur}
                canRecordCalls={canRecordCalls}
                isRecording={isRecording}
                startRecording={startRecording}
                stopRecording={stopRecording}
                addFriend={addFriend}
                endSession={endSession}
              />
            </div>

            {/* Goals Section for Stacked Layout */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                <Target className="w-5 h-5 mr-2" />
                Session Goals
              </h3>
              
              <div className="space-y-4">
                {/* Your Goal */}
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">Your Goal</label>
                  {goals.user ? (
                    <div className="bg-white/20 rounded-lg p-3 flex items-center justify-between">
                      <span className="text-white">{goals.user}</span>
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    </div>
                  ) : (
                    <form onSubmit={setGoal} className="flex space-x-2">
                      <input
                        type="text"
                        value={newGoal}
                        onChange={(e) => setNewGoal(e.target.value)}
                        placeholder="What will you focus on?"
                        className="flex-1 px-4 py-2 rounded-lg bg-white/20 text-white placeholder-white/60 border border-white/30 focus:border-white/50 focus:outline-none"
                      />
                      <button
                        type="submit"
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        Set
                      </button>
                    </form>
                  )}
                </div>

                {/* Partner Goal */}
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">Partner's Goal</label>
                  <div className="bg-white/20 rounded-lg p-3">
                    <span className="text-white">
                      {goals.partner || 'Waiting for partner to set their goal...'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : cameraLayout === 'stacked-with-chat' ? (
          /* Stacked with Chat Layout */
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Video Area - Full Width */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
                <div className="space-y-4">
                  {/* Partner Video (Top) */}
                  <div className="relative bg-gray-800 rounded-xl aspect-video overflow-hidden">
                    <video
                      ref={remoteVideoRef}
                      autoPlay
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    {!remoteStream && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-white text-center">
                          <div className="bg-purple-500 rounded-full p-4 w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                            <span className="font-bold text-xl">{session.partner.name[0].toUpperCase()}</span>
                          </div>
                          <p className="font-medium">{session.partner.name}</p>
                          <div className="flex items-center justify-center gap-1.5 mt-2 text-white/70 text-xs">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Connecting...</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="absolute top-2 left-2 bg-black/50 rounded px-2 py-1">
                      <span className="text-white text-xs font-medium">{session.partner.name}</span>
                    {reconnecting && (
                      <span className="ml-2 text-amber-300 text-xs">Reconnecting...</span>
                    )}
                    </div>
                  </div>

                  {/* User Video (Bottom) */}
                  <div className="relative bg-gray-800 rounded-xl aspect-video overflow-hidden">
                    <video
                      ref={localVideoRef}
                      autoPlay
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    {!localStream && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-white text-center">
                          <div className="bg-blue-500 rounded-full p-4 w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                            <span className="font-bold text-xl">{user.name[0].toUpperCase()}</span>
                          </div>
                          <p className="font-medium">{user.name} (You)</p>
                        </div>
                      </div>
                    )}
                    {!isVideoEnabled && localStream && (
                      <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                        <div className="text-white text-center">
                          <div className="bg-blue-500 rounded-full p-4 w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                            <span className="font-bold text-xl">{user.name[0].toUpperCase()}</span>
                          </div>
                          <p className="font-medium">{user.name} (You)</p>
                        </div>
                      </div>
                    )}
                    {mediaError && (
                      <div className="absolute inset-0 bg-gray-900/95 flex items-center justify-center p-4">
                        <div className="text-white text-center text-sm">
                          <VideoOff className="w-6 h-6 mx-auto mb-2 text-red-400" />
                          <p>{mediaError}</p>
                        </div>
                      </div>
                    )}
                    <div className="absolute top-2 left-2 bg-black/50 rounded px-2 py-1">
                      <span className="text-white text-xs font-medium">You</span>
                    </div>
                    {!isVideoEnabled && (
                      <div className="absolute top-2 right-2 bg-red-500 rounded-full p-1">
                        <VideoOff className="w-4 h-4 text-white" />
                      </div>
                    )}
                    {!isAudioEnabled && (
                      <div className="absolute bottom-2 right-2 bg-red-500 rounded-full p-1">
                        <MicOff className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                </div>

                  {/* Controls */}
                  <CallControls
                    isVideoEnabled={isVideoEnabled}
                    toggleVideo={toggleVideo}
                    isAudioEnabled={isAudioEnabled}
                    toggleAudio={toggleAudio}
                    canScreenShare={canScreenShare}
                    isScreenSharing={isScreenSharing}
                    toggleScreenShare={toggleScreenShare}
                    canBlur={canBlur}
                    isBlurEnabled={isBlurEnabled}
                    isBlurLoading={isBlurLoading}
                    toggleBlur={toggleBlur}
                    canRecordCalls={canRecordCalls}
                    isRecording={isRecording}
                    startRecording={startRecording}
                    stopRecording={stopRecording}
                    addFriend={addFriend}
                    endSession={endSession}
                  />
              </div>

              {/* Goals Section */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                  <Target className="w-5 h-5 mr-2" />
                  Session Goals
                </h3>
                
                <div className="space-y-4">
                  {/* Your Goal */}
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">Your Goal</label>
                    {goals.user ? (
                      <div className="bg-white/20 rounded-lg p-3 flex items-center justify-between">
                        <span className="text-white">{goals.user}</span>
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      </div>
                    ) : (
                      <form onSubmit={setGoal} className="flex space-x-2">
                        <input
                          type="text"
                          value={newGoal}
                          onChange={(e) => setNewGoal(e.target.value)}
                          placeholder="What will you focus on?"
                          className="flex-1 px-4 py-2 rounded-lg bg-white/20 text-white placeholder-white/60 border border-white/30 focus:border-white/50 focus:outline-none"
                        />
                        <button
                          type="submit"
                          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                          Set
                        </button>
                      </form>
                    )}
                  </div>

                  {/* Partner Goal */}
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">Partner's Goal</label>
                    <div className="bg-white/20 rounded-lg p-3">
                      <span className="text-white">
                        {goals.partner || 'Waiting for partner to set their goal...'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Chat Sidebar */}
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 h-96 flex flex-col relative">
                {/* Watermark Overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                  <img 
                    src="/watermark.png" 
                    alt="BodyDouble Watermark"
                    className="opacity-30 w-48 h-48 object-contain"
                  />
                </div>
                
                {/* Chat Content */}
                <div className="relative z-10 flex flex-col h-full">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Chat
                  </h3>
                  
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto space-y-3 mb-4">
                    {messages.map((message, index) => (
                      <div key={index} className={`flex ${message.from.id === user.id ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-xs px-3 py-2 rounded-lg ${
                          message.from.id === user.id 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-white/20 text-white'
                        }`}>
                          {message.type === 'goal' && (
                            <Target className="w-4 h-4 inline mr-1" />
                          )}
                          <p className="text-sm">{message.text}</p>
                          <p className="text-xs opacity-70 mt-1">
                            {new Date(message.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <form onSubmit={sendMessage} className="flex space-x-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 px-3 py-2 rounded-lg bg-white/20 text-white placeholder-white/60 border border-white/30 focus:border-white/50 focus:outline-none text-sm"
                    />
                    <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Send
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Default Side-by-Side Layout */
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Session Area */}
            <div className="lg:col-span-2 space-y-6">
              {/* Video Area */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* User Video */}
                <div className="relative bg-gray-800 rounded-xl aspect-video overflow-hidden">
                  <video
                    ref={localVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  {!localStream && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="bg-blue-500 rounded-full p-4 w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                          <span className="font-bold text-xl">{user.name[0].toUpperCase()}</span>
                        </div>
                        <p className="font-medium">{user.name} (You)</p>
                      </div>
                    </div>
                  )}
                  {!isVideoEnabled && localStream && (
                    <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="bg-blue-500 rounded-full p-4 w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                          <span className="font-bold text-xl">{user.name[0].toUpperCase()}</span>
                        </div>
                        <p className="font-medium">{user.name} (You)</p>
                      </div>
                    </div>
                  )}
                  {mediaError && (
                    <div className="absolute inset-0 bg-gray-900/95 flex items-center justify-center p-4">
                      <div className="text-white text-center text-sm">
                        <VideoOff className="w-6 h-6 mx-auto mb-2 text-red-400" />
                        <p>{mediaError}</p>
                      </div>
                    </div>
                  )}
                  <div className="absolute top-2 left-2 bg-black/50 rounded px-2 py-1">
                    <span className="text-white text-xs font-medium">You</span>
                  </div>
                  {!isVideoEnabled && (
                    <div className="absolute top-2 right-2 bg-red-500 rounded-full p-1">
                      <VideoOff className="w-4 h-4 text-white" />
                    </div>
                  )}
                  {!isAudioEnabled && (
                    <div className="absolute bottom-2 right-2 bg-red-500 rounded-full p-1">
                      <MicOff className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>

                {/* Partner Video */}
                <div className="relative bg-gray-800 rounded-xl aspect-video overflow-hidden">
                  <video
                    ref={remoteVideoRef}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  {!remoteStream && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="bg-purple-500 rounded-full p-4 w-16 h-16 mx-auto mb-2 flex items-center justify-center">
                          <span className="font-bold text-xl">{session.partner.name[0].toUpperCase()}</span>
                        </div>
                        <p className="font-medium">{session.partner.name}</p>
                        {connectionFailed ? (
                          <p className="mt-2 text-red-300 text-xs max-w-[14rem] mx-auto">
                            Couldn&apos;t connect to {session.partner.name}. Your networks may be
                            blocking the connection — try again, or switch off a VPN if you use one.
                          </p>
                        ) : (
                          <div className="flex items-center justify-center gap-1.5 mt-2 text-white/70 text-xs">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Connecting...</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="absolute top-2 left-2 bg-black/50 rounded px-2 py-1">
                    <span className="text-white text-xs font-medium">{session.partner.name}</span>
                    {reconnecting && (
                      <span className="ml-2 text-amber-300 text-xs">Reconnecting...</span>
                    )}
                  </div>
                </div>
              </div>

                {/* Controls */}
                <CallControls
                  isVideoEnabled={isVideoEnabled}
                  toggleVideo={toggleVideo}
                  isAudioEnabled={isAudioEnabled}
                  toggleAudio={toggleAudio}
                  canScreenShare={canScreenShare}
                  isScreenSharing={isScreenSharing}
                  toggleScreenShare={toggleScreenShare}
                  canBlur={canBlur}
                  isBlurEnabled={isBlurEnabled}
                  isBlurLoading={isBlurLoading}
                  toggleBlur={toggleBlur}
                  canRecordCalls={canRecordCalls}
                  isRecording={isRecording}
                  startRecording={startRecording}
                  stopRecording={stopRecording}
                  addFriend={addFriend}
                  endSession={endSession}
                  showChatToggle
                  showChat={showChat}
                  onToggleChat={() => setShowChat(!showChat)}
                />
            </div>
            </div>

            {/* Goals Section */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                <Target className="w-5 h-5 mr-2" />
                Session Goals
              </h3>
              
              <div className="space-y-4">
                {/* Your Goal */}
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">Your Goal</label>
                  {goals.user ? (
                    <div className="bg-white/20 rounded-lg p-3 flex items-center justify-between">
                      <span className="text-white">{goals.user}</span>
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    </div>
                  ) : (
                    <form onSubmit={setGoal} className="flex space-x-2">
                      <input
                        type="text"
                        value={newGoal}
                        onChange={(e) => setNewGoal(e.target.value)}
                        placeholder="What will you focus on?"
                        className="flex-1 px-4 py-2 rounded-lg bg-white/20 text-white placeholder-white/60 border border-white/30 focus:border-white/50 focus:outline-none"
                      />
                      <button
                        type="submit"
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        Set
                      </button>
                    </form>
                  )}
                </div>

                {/* Partner Goal */}
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">Partner's Goal</label>
                  <div className="bg-white/20 rounded-lg p-3">
                    <span className="text-white">
                      {goals.partner || 'Waiting for partner to set their goal...'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Chat Sidebar */}
            <div className={`${showChat ? 'block' : 'hidden'} lg:block`}>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 h-96 flex flex-col relative">
                {/* Watermark Overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                  <img 
                    src="/watermark.png" 
                    alt="BodyDouble Watermark"
                    className="opacity-30 w-48 h-48 object-contain"
                  />
                </div>
                
                {/* Chat Content */}
                <div className="relative z-10 flex flex-col h-full">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Chat
                  </h3>
                  
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto space-y-3 mb-4">
                    {messages.map((message, index) => (
                      <div key={index} className={`flex ${message.from.id === user.id ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-xs px-3 py-2 rounded-lg ${
                          message.from.id === user.id 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-white/20 text-white'
                        }`}>
                          {message.type === 'goal' && (
                            <Target className="w-4 h-4 inline mr-1" />
                          )}
                          <p className="text-sm">{message.text}</p>
                          <p className="text-xs opacity-70 mt-1">
                            {new Date(message.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <form onSubmit={sendMessage} className="flex space-x-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 px-3 py-2 rounded-lg bg-white/20 text-white placeholder-white/60 border border-white/30 focus:border-white/50 focus:outline-none text-sm"
                    />
                    <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Send
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
        </>

        {/* Session Info */}
        <div className="mt-6 bg-white/10 backdrop-blur-md rounded-2xl p-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Session Info</h3>
              <div className="space-y-2 text-white/80">
                <p><strong>Started:</strong> {new Date(session.startTime).toLocaleTimeString()}</p>
                <p><strong>Focus Style:</strong> {session.partner.focusStyle}</p>
                <p><strong>Work Type:</strong> {session.partner.workType}</p>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Tips for Body Doubling</h3>
              <ul className="space-y-1 text-white/80 text-sm">
                <li>• Share what you're working on</li>
                <li>• Take breaks together</li>
                <li>• Celebrate small wins</li>
                <li>• Be supportive and understanding</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionPage;
